import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/design-system/atoms/button';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';
import {
  AdminSideNavigation,
  AdminRoutes,
} from '@/features/admin/navigation/containers/admin-side-navigation';
import ContractWizardTimeline from '../contract-wizard-timeline/ContractWizardTimeline';
import { DatePicker } from '@/components/design-system/molecules/datepicker';

interface PickDatesFormProps {
  isOpen: boolean;
  onClose: () => void;
  onPrevious: () => void;
  onSubmit: (formData: DatesFormData) => void;
  isSubmitting?: boolean;
  initialData?: DatesFormData;
  colocFirstName?: string;
}

interface DatesFormData {
  moveInDate: Date | null;
  moveOutDate: Date | null;
}

const PickDatesForm: React.FC<PickDatesFormProps> = ({
  isOpen,
  onClose,
  onPrevious,
  onSubmit,
  isSubmitting = false,
  initialData,
}) => {
  const { t } = useTranslation();
  const [moveInDate, setMoveInDate] = useState<Date | null>(initialData?.moveInDate || null);
  const [moveOutDate, setMoveOutDate] = useState<Date | null>(initialData?.moveOutDate || null);

  const colocFirstName = sessionStorage.getItem('colocFirstName') || 'Coloc-first-name';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      moveInDate,
      moveOutDate,
    });
  };

  // Cast components to any to avoid TypeScript errors
  const IconSaxIconAny = IconSaxIcon as any;
  const AdminSideNavigationAny = AdminSideNavigation as any;
  const ContractWizardTimelineAny = ContractWizardTimeline as any;
  const DatePickerAny = DatePicker as any;

  if (!isOpen) return null;

  return (
    <div className="flex h-screen w-screen">
      {/* Sidebar */}
      <div className="flex-shrink-0">
        <AdminSideNavigationAny tabToSet={AdminRoutes.Contracts} />
      </div>

      {/* Main content */}
      <div className="flex h-full flex-1 flex-col bg-gray-50 p-6">
        {/* Breadcrumb and page title */}
        <div className="border-b border-gray-200">
          <div className="flex items-center text-[14px] font-medium">
            <span className="text-text-primaryLight">
              {t('js.features.admin.contracts.pick_dates_form.page_name')}
            </span>
            <IconSaxIconAny name="arrow-right-1" size={16} className="mx-2" />
            <span className="text-N900">
              {t('js.features.admin.contracts.pick_dates_form.select_dates')}
            </span>
          </div>
          <h1 className="mt-2 text-text-primaryHeavy text-[25px] font-bold leading-loose">
            {t('js.features.admin.contracts.pick_dates_form.create_contract')}
          </h1>
        </div>

        {/* Wizard timeline */}
        <div className="rounded-lg border-b border-gray-200 bg-white px-8 py-4">
          <ContractWizardTimelineAny currentStep={3} />
        </div>

        {/* Form content */}
        <div className="flex-1 overflow-auto">
          <form onSubmit={handleSubmit} className="mt-6">
            <div className="rounded-lg bg-white p-6 shadow-sm">
              <div className="mb-6">
                <h2
                  style={{ fontSize: '18px' }}
                  className="font-bold leading-relaxed text-text-primaryHeavy"
                >
                  {t('js.features.admin.contracts.pick_dates_form.moving_in_question', {
                    name: colocFirstName,
                  })}
                </h2>
                <p className="mt-1 text-[14px] font-medium text-text-primaryLight">
                  {t('js.features.admin.contracts.pick_dates_form.provide_dates')}
                </p>
              </div>

              <h3 className="mb-4 text-[16px] font-medium text-N900">
                {t('js.features.admin.contracts.pick_dates_form.dates_details')}
              </h3>

              <div className="mb-4 grid w-[500px] grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-[12px] font-medium text-N900">
                    {t('js.features.admin.contracts.pick_dates_form.move_in')}
                  </label>
                  <div className="w-full">
                    <DatePickerAny
                      type="date"
                      placeholder="Select date"
                      onChange={(date: Date) =>
                        setMoveInDate(new Date(date.toISOString().split('T')[0]))
                      }
                      value={moveInDate}
                      className="w-full"
                      label={t('js.features.admin.contracts.pick_dates_form.move_in')}
                      showLabel={false}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 flex justify-between text-[12px] font-medium text-N900">
                    {t('js.features.admin.contracts.pick_dates_form.move_out')}
                    <span className="text-[12px] text-text-primaryLight">
                      {t('js.features.admin.contracts.pick_dates_form.optional')}
                    </span>
                  </label>
                  <div className="w-full">
                    <DatePickerAny
                      type="date"
                      placeholder="Select date"
                      onChange={(date: Date) =>
                        setMoveOutDate(new Date(date.toISOString().split('T')[0]))
                      }
                      value={moveOutDate}
                      className="w-full"
                      label={t('js.features.admin.contracts.pick_dates_form.move_out')}
                      showLabel={false}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-4 flex justify-between">
                <button
                  type="button"
                  onClick={onClose}
                  className="mb-5 border-b bg-transparent text-[16px]"
                >
                  {t('js.features.admin.contracts.pick_dates_form.cancel')}
                </button>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={onPrevious}
                    style={{ backgroundColor: '#F3F4F5' }}
                    className="flex h-[48px] w-[128px] items-center justify-center gap-1 rounded-[8px] px-4 py-[12px] font-sans text-[16px] font-medium leading-[20px] text-[#212529] hover:bg-gray-200 disabled:bg-E50 disabled:text-E75"
                    disabled={isSubmitting}
                  >
                    {t('js.features.admin.contracts.pick_dates_form.previous')}
                  </button>
                  <button
                    type="submit"
                    className="flex h-[48px] items-center justify-center gap-1 rounded-[8px] bg-R300 px-4 py-[12px] font-sans text-[16px] font-medium leading-[20px] text-[#212529] hover:bg-R400 disabled:bg-E50 disabled:text-E75"
                    disabled={isSubmitting || !moveInDate}
                  >
                    {isSubmitting
                      ? t('js.features.admin.contracts.pick_dates_form.creating_contract')
                      : t('js.features.admin.contracts.pick_dates_form.create_contract_button')}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PickDatesForm;
